import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CompanyAddressInformation = {
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
};

export type CompanyContactInformation = {
  phone: string;
  email: string;
};

export type CompanyProfileData = {
  bp_location_owner_id?: string | number | null;
  bp_location_name: string;
  bp_location_type: string;
  bp_location_currency: string;
  bp_location_is_default: boolean;
  bp_business_description: string;
  /** Saved logo URL, or a data URI picked but not saved yet; '' when none. */
  bp_location_logo?: string;
  bp_location_address_information: CompanyAddressInformation;
  bp_location_contact_information: CompanyContactInformation;
};

export interface BackofficeCompanyProfileSliceObject {
  loading: boolean;
  error?: string | null;
  data: CompanyProfileData;
  lastFetchedAt?: number | null;
}

const emptyProfile: CompanyProfileData = {
  bp_location_owner_id: null,
  bp_location_name: '',
  bp_location_type: 'General Shop',
  bp_location_currency: 'KES',
  bp_location_is_default: false,
  bp_business_description: '',
  bp_location_address_information: {
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  },
  bp_location_contact_information: {
    phone: '',
    email: '',
  },
};

const initialState: BackofficeCompanyProfileSliceObject = {
  loading: false,
  error: null,
  data: emptyProfile,
  lastFetchedAt: null,
};

const companyProfileSlice = createSlice({
  name: 'backofficeCompanyProfile',
  initialState,
  reducers: {
    setCompanyProfileLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCompanyProfileError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCompanyProfile: (state, action: PayloadAction<CompanyProfileData>) => {
      state.data = action.payload;
      state.lastFetchedAt = Date.now();
      state.error = null;
    },
    mergeCompanyProfile: (
      state,
      action: PayloadAction<Partial<CompanyProfileData>>,
    ) => {
      state.data = { ...state.data, ...action.payload } as CompanyProfileData;
    },
    resetCompanyProfile: state => {
      state.data = emptyProfile;
      state.error = null;
      state.loading = false;
      state.lastFetchedAt = null;
    },
    ensureCompanyProfileInitialized: state => {
      // Defensive hydration for persisted state migrations
      const d = state.data as any;
      if (!d || typeof d !== 'object') state.data = { ...emptyProfile };
      if (!state.data.bp_location_address_information)
        state.data.bp_location_address_information = {
          ...emptyProfile.bp_location_address_information,
        };
      if (!state.data.bp_location_contact_information)
        state.data.bp_location_contact_information = {
          ...emptyProfile.bp_location_contact_information,
        };
      if (!state.data.bp_location_type)
        state.data.bp_location_type = 'General Shop';
      if (!state.data.bp_location_currency)
        state.data.bp_location_currency = 'KES';
      if (typeof state.data.bp_location_is_default !== 'boolean')
        state.data.bp_location_is_default = false;
    },
  },
});

export const {
  setCompanyProfileLoading,
  setCompanyProfileError,
  setCompanyProfile,
  mergeCompanyProfile,
  resetCompanyProfile,
  ensureCompanyProfileInitialized,
} = companyProfileSlice.actions;

export default companyProfileSlice.reducer;
